import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, Search, Gamepad2, Wrench, Clock, CheckCircle, 
  AlertTriangle, ChevronRight, Star
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GuidesPage = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const [guides, setGuides] = useState([]);
  const [consoles, setConsoles] = useState([]);
  const [selectedConsole, setSelectedConsole] = useState(null);
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [guidesRes, consolesRes] = await Promise.all([
          axios.get(`${API_URL}/api/guides`),
          axios.get(`${API_URL}/api/gaming/consoles`)
        ]);
        setGuides(guidesRes.data);
        setConsoles(consolesRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const fetchGuideDetails = async (guideId) => {
    try {
      const response = await axios.get(`${API_URL}/api/guides/${guideId}`);
      setSelectedGuide(response.data);
    } catch (error) {
      console.error('Error fetching guide:', error);
    }
  };

  const filteredGuides = guides.filter(guide => {
    const matchesSearch = guide.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesConsole = !selectedConsole || guide.console === selectedConsole;
    return matchesSearch && matchesConsole;
  });

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy': return 'text-[#10B981] bg-[#10B981]/10';
      case 'medium': return 'text-[#F59E0B] bg-[#F59E0B]/10';
      case 'hard': return 'text-[#EF4444] bg-[#EF4444]/10';
      default: return 'text-[#52525B] bg-[#F5F5F7]';
    }
  };

  const getDifficultyLabel = (difficulty) => {
    switch (difficulty) {
      case 'easy': return 'Fácil';
      case 'medium': return 'Medio';
      case 'hard': return 'Difícil';
      default: return difficulty;
    }
  };

  if (selectedGuide) {
    return (
      <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900' : 'bg-[#F5F5F7]'}`}>
        <header className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-[#E5E7EB]'} border-b`}>
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
            <button
              onClick={() => setSelectedGuide(null)}
              className="flex items-center gap-2 text-[#52525B] dark:text-gray-400 hover:text-[#0A0A0A] dark:hover:text-white"
            >
              <ArrowLeft className="w-4 h-4" />
              Volver a guías
            </button>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Guide header */}
            <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-xl border ${theme === 'dark' ? 'border-gray-700' : 'border-[#E5E7EB]'} p-6 mb-6`}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className={`text-2xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : ''}`}>
                    {selectedGuide.title}
                  </h1>
                  <div className="flex items-center gap-3 text-sm">
                    <span className={`px-2 py-1 rounded-full ${getDifficultyColor(selectedGuide.difficulty)}`}>
                      {getDifficultyLabel(selectedGuide.difficulty)}
                    </span>
                    <span className="flex items-center gap-1 text-[#52525B] dark:text-gray-400">
                      <Clock className="w-4 h-4" />
                      {selectedGuide.time_minutes} min
                    </span>
                    <span className="flex items-center gap-1 text-[#10B981]">
                      <CheckCircle className="w-4 h-4" />
                      {selectedGuide.solution_rate}% éxito
                    </span>
                  </div>
                </div>
              </div>

              {selectedGuide.tools_needed?.length > 0 && (
                <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-[#F5F5F7]'}`}>
                  <p className="font-medium mb-2 dark:text-white">Herramientas necesarias:</p>
                  <ul className="list-disc list-inside text-sm text-[#52525B] dark:text-gray-400">
                    {selectedGuide.tools_needed.map((tool, i) => (
                      <li key={i}>{tool}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Steps */}
            <div className="space-y-4">
              {selectedGuide.steps?.map((step, index) => (
                <motion.div
                  key={step.step}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-[#E5E7EB]'} rounded-xl border p-6`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#0047FF] rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                      {step.step}
                    </div>
                    <div className="flex-1">
                      <h3 className={`font-semibold mb-2 ${theme === 'dark' ? 'text-white' : ''}`}>
                        {step.title}
                      </h3>
                      <p className="text-[#52525B] dark:text-gray-400">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* If not fixed */}
            {selectedGuide.if_not_fixed && (
              <div className="mt-6 p-6 bg-[#F59E0B]/10 border border-[#F59E0B]/20 rounded-xl">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-[#F59E0B] flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold mb-1 text-[#F59E0B]">¿No ha funcionado?</h4>
                    <p className="text-sm text-[#52525B] dark:text-gray-400">
                      {selectedGuide.if_not_fixed}
                    </p>
                    <Link to="/warranty/new">
                      <Button className="mt-4 bg-[#0047FF] hover:bg-[#003BCC]">
                        Solicitar Reparación Profesional
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </main>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900' : 'bg-[#F5F5F7]'}`}>
      <header className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-[#E5E7EB]'} border-b`}>
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link
            to="/dashboard"
            className="flex items-center gap-2 text-[#52525B] dark:text-gray-400 hover:text-[#0A0A0A] dark:hover:text-white"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="text-center mb-8">
            <h1 className={`text-3xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : ''}`}>
              Guías de Diagnóstico DIY
            </h1>
            <p className="text-[#52525B] dark:text-gray-400">
              Prueba estas soluciones antes de enviar tu producto a reparación
            </p>
          </div>

          {/* Search and filter */}
          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A1AA]" />
              <input
                type="text"
                placeholder="Buscar guías..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full pl-10 pr-4 py-2 rounded-lg border ${
                  theme === 'dark' 
                    ? 'bg-gray-800 border-gray-700 text-white' 
                    : 'bg-white border-[#E5E7EB]'
                }`}
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setSelectedConsole(null)}
                className={`px-4 py-2 rounded-lg text-sm ${
                  !selectedConsole 
                    ? 'bg-[#0047FF] text-white' 
                    : theme === 'dark' ? 'bg-gray-800 text-gray-300' : 'bg-white text-[#52525B]'
                }`}
              >
                Todos
              </button>
              {consoles.map((console) => (
                <button
                  key={console.id}
                  onClick={() => setSelectedConsole(console.id)}
                  className={`px-4 py-2 rounded-lg text-sm ${
                    selectedConsole === console.id 
                      ? 'bg-[#0047FF] text-white' 
                      : theme === 'dark' ? 'bg-gray-800 text-gray-300' : 'bg-white text-[#52525B]'
                  }`}
                >
                  {console.name}
                </button>
              ))}
            </div>
          </div>

          {/* Guides grid */}
          {loading ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-2 border-[#0047FF] border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredGuides.map((guide, index) => (
                <motion.div
                  key={guide.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => fetchGuideDetails(guide.id)}
                  className={`${
                    theme === 'dark' ? 'bg-gray-800 border-gray-700 hover:border-[#0047FF]' : 'bg-white border-[#E5E7EB] hover:border-[#0047FF]'
                  } border rounded-xl p-5 cursor-pointer transition-all hover:-translate-y-1 hover:shadow-lg`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      guide.console.includes('playstation') ? 'bg-[#003087]/10' :
                      guide.console.includes('nintendo') ? 'bg-[#E60012]/10' :
                      guide.console.includes('xbox') ? 'bg-[#107C10]/10' : 'bg-[#0047FF]/10'
                    }`}>
                      <Wrench className={`w-6 h-6 ${
                        guide.console.includes('playstation') ? 'text-[#003087]' :
                        guide.console.includes('nintendo') ? 'text-[#E60012]' :
                        guide.console.includes('xbox') ? 'text-[#107C10]' : 'text-[#0047FF]'
                      }`} />
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${getDifficultyColor(guide.difficulty)}`}>
                      {getDifficultyLabel(guide.difficulty)}
                    </span>
                  </div>

                  <h3 className={`font-semibold mb-2 ${theme === 'dark' ? 'text-white' : ''}`}>
                    {guide.title}
                  </h3>

                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-1 text-[#52525B] dark:text-gray-400">
                      <Clock className="w-4 h-4" />
                      {guide.time_minutes} min
                    </span>
                    <span className="flex items-center gap-1 text-[#10B981]">
                      <Star className="w-4 h-4 fill-current" />
                      {guide.solution_rate}% éxito
                    </span>
                  </div>

                  <div className="flex items-center justify-end mt-3 text-[#0047FF] text-sm">
                    Ver guía <ChevronRight className="w-4 h-4" />
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {filteredGuides.length === 0 && !loading && (
            <div className="text-center py-12">
              <Gamepad2 className="w-12 h-12 text-[#A1A1AA] mx-auto mb-4" />
              <p className="text-[#52525B] dark:text-gray-400">No se encontraron guías</p>
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default GuidesPage;
